import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Library, BarChart3, User, GraduationCap, Layers, Repeat, Timer, Menu, X, LogOut, Gift, ShieldCheck, Sparkles, BookMarked, Lightbulb, GalleryVerticalEnd } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/context/LanguageContext";
import { useOnboarding } from "@/context/OnboardingContext";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const buildNavGroups = (user) => {
    const accountItems = [
        { to: "/profile", labelKey: "nav.profile", icon: User, testid: "nav-profile" },
    ];
    if (user?.is_admin) {
        accountItems.push({ to: "/admin", labelKey: "nav.admin", icon: ShieldCheck, testid: "nav-admin" });
    }
    return [
        {
            labelKey: "nav.groupLibrary",
            items: [
                { to: "/workspaces", labelKey: "nav.workspaces", icon: GalleryVerticalEnd, testid: "nav-workspaces" },
                { to: "/library", labelKey: "nav.library", icon: Library, testid: "nav-library" },
                { to: "/analytics", labelKey: "nav.analytics", icon: BarChart3, testid: "nav-analytics" },
            ],
        },
        {
            labelKey: "nav.groupLearn",
            items: [
                { to: "/learn", labelKey: "nav.learnDashboard", icon: GraduationCap, testid: "nav-learn" },
                { to: "/glossary", labelKey: "glossary.nav", icon: BookMarked, testid: "nav-glossary" },
                { to: "/study", labelKey: "nav.flashcards", icon: Layers, testid: "nav-flashcards" },
                { to: "/review", labelKey: "nav.review", icon: Repeat, testid: "nav-review" },
                { to: "/exam", labelKey: "nav.examMode", icon: Timer, testid: "nav-exam" },
            ],
        },
        {
            labelKey: "nav.groupEarn",
            items: [
                { to: "/ambassador", labelKey: "nav.ambassador", icon: Gift, testid: "nav-ambassador" },
            ],
        },
        {
            labelKey: "nav.groupAccount",
            items: accountItems,
        },
    ];
};

const NavInner = ({ onNavigate }) => {
    const { user, logout } = useAuth();
    const { t } = useTranslation();
    const { startTour } = useOnboarding();
    const navigate = useNavigate();
    const navGroups = buildNavGroups(user);
    const showUpgrade = (user?.tier || "free").toLowerCase() === "free";
    return (
        <div className="flex h-full flex-col">
            <div className="flex items-center gap-2 px-5 py-5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                    <GraduationCap className="h-5 w-5" />
                </div>
                <div className="leading-tight">
                    <div className="font-sans-display text-sm font-semibold text-t1">LearnOS</div>
                    <div className="mono-label text-t4">{t("nav.tagline")}</div>
                </div>
            </div>

            <nav className="mt-1 flex-1 space-y-5 overflow-y-auto px-3 pb-3">
                {navGroups.map((group) => (
                    <div key={group.labelKey}>
                        <div className="mono-label px-3 pb-2 text-t4">{t(group.labelKey)}</div>
                        <div className="space-y-1">
                            {group.items.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <NavLink
                                        key={item.to}
                                        to={item.to}
                                        end
                                        onClick={onNavigate}
                                        data-testid={item.testid}
                                        className={({ isActive }) =>
                                            cn(
                                                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
                                                isActive
                                                    ? "bg-surface-3 text-t1"
                                                    : "text-t3 hover:bg-surface-3/60 hover:text-t1"
                                            )
                                        }
                                    >
                                        <Icon className="h-[18px] w-[18px]" />
                                        {t(item.labelKey)}
                                    </NavLink>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </nav>

            <button
                data-testid="sidebar-guide-button"
                onClick={() => { onNavigate && onNavigate(); startTour(); }}
                className="mx-3 mb-2 flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-t3 transition-colors hover:bg-surface-3/60 hover:text-t1"
            >
                <Lightbulb className="h-4 w-4 text-[hsl(var(--primary))]" />
                <span className="text-sm font-medium">{t("onboarding.guide")}</span>
            </button>

            {showUpgrade && (
                <button
                    data-testid="sidebar-upgrade-button"
                    onClick={() => { onNavigate && onNavigate(); navigate("/pricing"); }}
                    className="mx-3 mb-2 flex items-center gap-2.5 rounded-xl border border-[hsl(var(--primary))]/35 bg-[hsl(var(--primary))]/10 px-3 py-2.5 text-left transition-colors hover:bg-[hsl(var(--primary))]/15"
                >
                    <Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" />
                    <span className="text-sm font-medium text-t1">{t("nav.upgrade")}</span>
                </button>
            )}

            <div className="mx-3 mb-3 rounded-xl border border-border bg-surface-2 p-3">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-3 text-t1 font-sans-display text-xs font-semibold uppercase">
                        {(user?.name || "L").slice(0, 1)}
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-medium text-t1">{user?.name}</div>
                        <div className="truncate mono-label text-[9px] text-t4">{user?.tier} {t("profile.planSuffix")}</div>
                    </div>
                    <LanguageSwitcher variant="compact" align="start" testId="language-switcher-sidebar" />
                </div>
                <Button
                    data-testid="logout-button"
                    variant="secondary"
                    size="sm"
                    className="mt-3 w-full justify-center gap-2 bg-surface-3/60"
                    onClick={() => {
                        logout();
                        navigate("/login");
                    }}
                >
                    <LogOut className="h-3.5 w-3.5" /> {t("nav.signOut")}
                </Button>
            </div>
        </div>
    );
};

export const Sidebar = () => {
    return (
        <aside className="hidden w-[260px] shrink-0 border-r border-border bg-surface-1 lg:block">
            <NavInner />
        </aside>
    );
};

export const MobileTopBar = () => {
    const [open, setOpen] = useState(false);
    const { startTour } = useOnboarding();
    return (
        <div className="lg:hidden">
            <div className="flex items-center justify-between border-b border-border bg-surface-1 px-4 py-3">
                <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                        <GraduationCap className="h-4 w-4" />
                    </div>
                    <span className="font-sans-display text-sm font-semibold text-t1">LearnOS</span>
                </div>
                <div className="flex items-center gap-1">
                    <Button data-testid="mobile-guide-button" variant="ghost" size="icon" onClick={startTour} aria-label="LearnOS Guide">
                        <Lightbulb className="h-5 w-5" />
                    </Button>
                    <LanguageSwitcher variant="compact" testId="language-switcher-mobile" />
                    <Button data-testid="mobile-menu-button" variant="ghost" size="icon" onClick={() => setOpen((v) => !v)}>
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </Button>
                </div>
            </div>
            {open && (
                <div className="fixed inset-0 z-50 lg:hidden" onClick={() => setOpen(false)}>
                    <div className="absolute inset-0 bg-black/60" />
                    <div className="absolute left-0 top-0 h-full w-[260px] border-r border-border bg-surface-1" onClick={(e) => e.stopPropagation()}>
                        <NavInner onNavigate={() => setOpen(false)} />
                    </div>
                </div>
            )}
        </div>
    );
};
